import React, { useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Flame, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import GasCylinderIcon from "./icons/GasCylinderIcon";

const KIND_ICONS = { bulk: Flame, cylinder: GasCylinderIcon, station: MapPin };

/**
 * HeroImagePicker: subir/quitar imagen hero por empresa para cada kind.
 *
 *  - `value`: URL actual (o null)
 *  - `defaultMode`: bool, true cuando el admin marcó "Sin imagen"
 *  - `onChange(url, useDefault)`: callback con la URL y el flag actualizado
 *  - `kind`: "bulk" | "cylinder" | "station" — solo se usa para el icono fallback
 *  - `label`: título del card (ej. "Autotanque / Gas a granel")
 *  - `description`: copy explicativo
 */
export default function HeroImagePicker({ value, defaultMode = false, onChange, kind = "bulk", label, description, testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const Icon = KIND_ICONS[kind] || Flame;
  const showingDefault = defaultMode || !value;

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Formato no permitido (usa jpg, png, webp o gif)");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen supera 3MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post("/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      onChange?.(fullUrl, false);
      toast.success("Imagen subida. Guarda los cambios para aplicarla.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error subiendo imagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const toggleDefault = (checked) => {
    if (checked) onChange?.(null, true);
    else onChange?.(value || null, false);
  };

  return (
    <div className="rounded-xl border border-neutral-200 p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Label className="text-sm font-semibold">{label}</Label>
          {description && <p className="text-xs text-neutral-500 mt-0.5">{description}</p>}
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showingDefault}
            onChange={(e) => toggleDefault(e.target.checked)}
            className="h-4 w-4 accent-[color:var(--company-primary)]"
            data-testid={`${testId}-default-checkbox`}
          />
          Sin imagen (usar icono por defecto)
        </label>
      </div>

      <div className="mt-3 flex items-center gap-4">
        {/* Preview */}
        <div className="h-20 w-28 rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50 flex items-center justify-center shrink-0">
          {value && !showingDefault ? (
            <img
              src={value}
              alt={label}
              className="h-full w-full object-cover"
              data-testid={`${testId}-preview`}
            />
          ) : (
            <div
              className="h-full w-full flex items-center justify-center"
              style={{ backgroundColor: "var(--company-primary, #0A0A0A)", color: "var(--company-primary-contrast, #fff)" }}
              data-testid={`${testId}-default-preview`}
            >
              <Icon size={28} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testId}-input`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-9 gap-2"
            data-testid={`${testId}-upload-btn`}
          >
            {uploading
              ? <Loader2 size={14} className="animate-spin" />
              : <Upload size={14} />}
            {uploading ? "Subiendo..." : value ? "Reemplazar" : "Subir imagen"}
          </Button>
          {value && !showingDefault && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-9 gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onChange?.(null, true)}
              data-testid={`${testId}-remove-btn`}
            >
              <Trash2 size={14} /> Quitar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
